import { useId, useMemo, useState } from 'react';

import type { Booking, BusinessDetail } from '../api/schemas';
import { customerNameOf, staffNameOf } from '../lib/bookingPresentation';
import { matchesBookingSearch } from '../lib/bookings';
import { formatTime, isSameDay } from '../lib/format';
import { serviceName } from '../lib/useBusiness';

type ChargeBookingPickerProps = {
  /** Already narrowed to the visits still to charge (lib/bookings.ts: bookingsToCharge). */
  bookings: Booking[];
  business: BusinessDetail | undefined;
  selectedBookingId: string | null;
  onSelect: (booking: Booking | null) => void;
};

const STATUS_WORD: Partial<Record<Booking['status'], string>> = {
  confirmed: 'Reservó',
  completed: 'Llegó',
};

/**
 * "Vincular reserva", name first: the counter knows who is standing in front
 * of it, not which 9:00 they came to. Renders nothing when there is nothing
 * left to charge (docs/FLOWS.md §1).
 */
export function ChargeBookingPicker({ bookings, business, selectedBookingId, onSelect }: ChargeBookingPickerProps) {
  const [query, setQuery] = useState('');
  const searchId = useId();
  const listId = useId();
  const now = useMemo(() => new Date(), []);
  const selected = bookings.find((booking) => booking.id === selectedBookingId);
  const matches = bookings.filter((booking) =>
    matchesBookingSearch(booking, query, serviceName(business, booking.serviceId)),
  );

  if (bookings.length === 0) return null;

  if (selected) {
    return (
      <div className="booking-pick">
        <p className="pos-pane-label">Vincular reserva</p>
        <div className="booking-pick-chosen">
          <BookingOption booking={selected} business={business} now={now} />
          <button type="button" className="linklike" onClick={() => onSelect(null)}>
            Cambiar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-pick">
      <label className="pos-pane-label" htmlFor={searchId}>
        Vincular reserva
      </label>
      <input
        id={searchId}
        type="search"
        className="booking-pick-search"
        placeholder="Busca por nombre"
        autoComplete="off"
        value={query}
        aria-controls={listId}
        onChange={(event) => setQuery(event.target.value)}
      />
      <ul id={listId} className="booking-pick-list" role="listbox" aria-label="Reservas por cobrar">
        {matches.length === 0 ? (
          <li className="row-sub booking-pick-empty">Nadie con ese nombre por cobrar.</li>
        ) : (
          matches.map((booking) => (
            <li key={booking.id} role="option" aria-selected={false}>
              <button type="button" className="booking-pick-option" onClick={() => onSelect(booking)}>
                <BookingOption booking={booking} business={business} now={now} />
              </button>
            </li>
          ))
        )}
      </ul>
    </div>
  );
}

function BookingOption({ booking, business, now }: { booking: Booking; business: BusinessDetail | undefined; now: Date }) {
  const name = customerNameOf(booking) ?? 'Sin nombre';
  const when = isSameDay(new Date(booking.startsAt), now)
    ? formatTime(booking.startsAt)
    : `${new Date(booking.startsAt).toLocaleDateString('es-PA', { weekday: 'short', day: 'numeric' })}, ${formatTime(booking.startsAt)}`;
  const details = [when, serviceName(business, booking.serviceId), staffNameOf(booking, business)].filter(Boolean);
  return (
    <>
      <span className="booking-pick-initial" aria-hidden="true">
        {name.charAt(0)}
      </span>
      <span className="booking-pick-text">
        <span className="booking-pick-name">{name}</span>
        <span className="row-sub">{details.join(' · ')}</span>
      </span>
      <span className="status" data-tone={booking.status === 'completed' ? 'good' : undefined}>
        {STATUS_WORD[booking.status]}
      </span>
    </>
  );
}
